import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

import { useEffect } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { usePostHog } from 'posthog-js/react';

import { IndexParameterInput, Model, Parameter, Value } from '@arra/core';
import { FormEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../ui/Button';
import Markdown from '../../../ui/Markdown';
import Parameters from './../../../project/Parameters';
import keyify from './../../../project/parameters/keyify';
import { getServiceType } from '../../../analytics/posthogEvents';

interface CreateProjectModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onClose?: any;
  onSave?: (values: IndexParameterInput[], modelType: string) => void;
  model: Model;
}

const CreateProjectModal = ({
  open,
  setOpen,
  onSave,
  onClose,
  model,
}: CreateProjectModalProps) => {
  const { t } = useTranslation(['common', 'model']);
  const posthog = usePostHog();

  // 0 = intro, 1 = parameters
  const [step, setStep] = useState(0);

  const [initialParameters, setInitialParameters] = useState([] as Parameter[]);
  const [parameterValues, setParameterValues] = useState(
    [] as IndexParameterInput[],
  );

  const [savedParameters, setSavedParameters] = useSessionStorage<
    string | null
  >(`new_project_${model.type}`, null);
  
  const called = useRef(false);
  const autofocusFirst = useRef(true);

  const isValueCompleted = (value: Value | undefined) => {
    if (value === undefined || value === null) {
      return false;
    }
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return true;
  };

  const getParameterCompletion = (values: IndexParameterInput[]) => {
    const totalParameters = initialParameters.length;
    const completedParameters = values.filter((parameter) =>
      isValueCompleted(parameter.value),
    ).length;
    const completionRate =
      totalParameters === 0 ? 0 : completedParameters / totalParameters;
    return {
      totalParameters,
      completedParameters,
      completionRate,
    };
  };

  const captureStepEvent = (action: string) => {
    const completion = getParameterCompletion(parameterValues);
    posthog?.capture('project_creation step action', {
      step_name: step === 0 ? 'project_creation_intro' : 'project_creation_parameters',
      step_order: step + 1,
      action,
      model_type: getServiceType(model.type),
      ...completion,
    });
  };

  useEffect(() => {
    let parameters: Parameter[] = JSON.parse(JSON.stringify(model?.parameters));
    if (savedParameters) {
      const savedParametersValues: IndexParameterInput[] =
        JSON.parse(savedParameters);
      parameters.map((parameter) => {
        const savedParameter = savedParametersValues.find(
          (savedParameterValues) =>
            savedParameterValues.index === parameter.index,
        );
        if (savedParameter) {
          parameter.value = savedParameter.value;
        }
        return parameter;
      });
    }
    setInitialParameters(parameters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model?.parameters]);

  useEffect(() => {
    if (!open) {
      return;
    }
    posthog?.capture('project_creation step viewed', {
      step_name: step === 0 ? 'project_creation_intro' : 'project_creation_parameters',
      step_order: step + 1,
      project_type: model.type,
      model_type: getServiceType(model.type),
      ...getParameterCompletion(parameterValues),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step]);

  if (initialParameters.length === 0) return null;

  const resetRefs = () => {
    called.current = false;
    autofocusFirst.current = true;
  };

  const handleClose = () => {
    resetRefs();
    onClose && onClose();
    setOpen(false);
    setStep(0); // start from intro next time
  };

  const handleCancel = () => {
    captureStepEvent('cancel');
    setSavedParameters(null);
    handleClose();
  };

  const handleSave = () => {
    captureStepEvent('create');
    resetRefs();
    setSavedParameters(null);
    onSave && onSave(parameterValues, model.type);
  };

  const handleParameterUpdated = (index: number, value: Value) => {
    if (autofocusFirst) {
      autofocusFirst.current = false;
    }
    const parameterValuesUpdated: IndexParameterInput[] = [
      ...parameterValues.filter((p) => p.index !== index),
      { type: 'index', index, value },
    ];
    setParameterValues(parameterValuesUpdated);
    setSavedParameters(JSON.stringify(parameterValuesUpdated));
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSave();
  };

  const handleBack = () => {
    captureStepEvent('back');
    setStep((s) => Math.max(0, s - 1));
  };

  const handleNext = () => {
    captureStepEvent('next');
    setStep(1);
  };

  const modelSingularName = t(`model:${keyify(model.name)}.singularName`);
  const modelIntroText = t(`model:${keyify(model.name)}.introText`);

  return (
    <Dialog open={open} onClose={handleClose} scroll="paper" id="create_modal">
      {/* STEP 1: Intro */}
      {step === 0 && (
        <>
          <DialogTitle id="scroll-dialog-title">
            {t('common:create-project-modal.title', {
              modelName: modelSingularName,
            })}
          </DialogTitle>
          <DialogContent>
            <Markdown>{modelIntroText}</Markdown>
          </DialogContent>
          <DialogActions
            sx={{ position: 'sticky', bottom: 0, background: 'white' }}
          >
            <Button type="button" onClick={handleCancel}>
              {t(`common:cancel`)}
            </Button>
            <Button
              type="button"
              onClick={handleNext}
              variant="contained"
              color="primary"
            >
              {t('common:next')}
            </Button>
          </DialogActions>
        </>
      )}

      {/* STEP 2: Parameters */}
      {step === 1 && (
        <form onSubmit={handleFormSubmit}>
          <DialogTitle id="scroll-dialog-title">
            {t(`common:create-project-modal.title`, {
              modelName: modelSingularName,
            })}
          </DialogTitle>
          <DialogContent dividers>
            <DialogContentText
              sx={{
                fontWeight: '400',
                fontSize: '16px',
                margin: '16px 0 32px',
              }}
            >
              {t(`common:create-project-modal.introduction`)}
            </DialogContentText>
            <DialogContentText
              id="scroll-dialog-description"
              tabIndex={-1}
              component="div"
            >
              <Parameters
                autofocusFirst={autofocusFirst.current}
                parameters={initialParameters || []}
                model={model}
                style={{ padding: 0 }}
                onUpdateParameter={handleParameterUpdated}
                debounced={false}
                isValidationVisible={false}
                isCreateProject={true}
              />
            </DialogContentText>
          </DialogContent>
          <DialogActions
            sx={{ position: 'sticky', bottom: 0, background: 'white' }}
          >
            <Button type="button" onClick={handleCancel}>
              {t(`common:cancel`)}
            </Button>
            <Button type="button" onClick={handleBack}>
              {t('common:back')}
            </Button>
            <Button type="submit" variant="contained" color="primary">
              {t(`common:create-project-modal.action`, {
                modelName: modelSingularName,
              })}
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};

export default CreateProjectModal;
